import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useNavigate } from 'react-router-dom';
import { Collapse } from 'reactstrap';
import dayjs from 'dayjs';

import { AuthTags } from '~constants/authTags';
import { AppointmentStatuses } from '~constants/maps';

import { dateInputFieldFormat } from '~libs/dayjs';
import { useAllSearchParams, useSearch } from '~libs/searchParams';

import { Button, ClickableIcon } from '~components';
import ContainerBanner from '~components/ContainerBanner';
import ContainerHeader from '~components/ContainerHeader';
import ErrorResponse from '~components/ErrorResponse';
import ExportDialog from '~components/ExportDialog';
import { SearchBar } from '~components/form-fields/search';
import Icon, {
  faBusinessTime,
  faFileExcel,
  faInfoCircle,
  faMagnifyingGlass,
  faPlus,
  faTableList,
} from '~components/icon';
import Link from '~components/Link';
import { Row, SortState, TableView } from '~components/table-view';
import BulkUpdatePackageWizard from '~appointments/components/bulk-update-package-wizard';

import exportFields from '~appointments/forms/appointments/export';
import { getAllColumns, getCommonColumns } from '~appointments/forms/appointments/list';
import { allFields, commonFields } from '~appointments/forms/appointments/search';

import { exportAll, getAll, getEstimate } from '~appointments/actions/appointments';
import { getAll as getServiceTypes } from '~appointments/actions/serviceTypes';
import { getAll as getStatuses } from '~appointments/actions/statuses';
import { getAll as getClients } from '~main/actions/clients';
import { getAll as getClientPackages } from '~main/actions/clients/clientPackages';
import { getAll as getClientReferrers } from '~main/actions/clients/clientReferrers';
import { setTitle } from '~main/actions/login';

import { clearAppointments, clearMessage } from '~appointments/reducers/appointments';

import {
  selectAppointmentLoading,
  selectAppointmentRowEstimate,
  selectAppointments,
  selectAppointmentsTotal,
  selectErrorResponse,
} from '~appointments/selectors/appointments';
import { selectUserTags } from '~main/selectors/login';

const Appointments: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const tags = useAppSelector(selectUserTags);
  const REDIRECT_URL = process.env.REACT_APP_TPONE_URL;
  const authTagUpdate = AuthTags.APPOINTMENTS.UPDATE;
  const authTagViewAll = AuthTags.APPOINTMENTS.VIEWALL;
  const authTagExport = AuthTags.APPOINTMENTS.DOWNLOAD;
  const authTagBulkUpdate = AuthTags.APPOINTMENTS.BULKUPDATE;

  const searchParamValues = useAllSearchParams();

  const initialSortState: SortState = [
    {
      id: 'last_updated',
      direction: 'desc',
    },
  ];

  const { client_id, user_id } = searchParamValues;
  const initialSearchState: any = {
    date: { gte: dayjs().format(dateInputFieldFormat) },
    status_id: Object.entries(AppointmentStatuses)
      .map(([, value]) => (value < 8 ? value : false))
      .filter(Boolean),
    client_id: client_id ?? undefined,
    user_id: user_id ?? undefined,
  };

  // Check for simplified/all view, match "Care Worker" view, to be removed upon close of TP1. (╯°□°）╯︵ ┻━┻
  const hasViewAll = tags.includes(AuthTags.APPOINTMENTS.VIEWALL);

  const openAppointment = useCallback((row: Row) => navigate(`/appointments/${row.id}`), [navigate]);
  const getAppointmentHref = useCallback((row: Row) => `/appointments/${row.id}`, []);
  const columns = useMemo(
    () => (hasViewAll ? getAllColumns(openAppointment, getAppointmentHref) : getCommonColumns(openAppointment, getAppointmentHref)),
    [hasViewAll, openAppointment, getAppointmentHref],
  );

  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearch('search', initialSearchState);

  const [exportDialogOpen, setExportDialogOpen] = useState<boolean>(false);
  const [bulkUpdateWizardOpen, setBulkUpdateWizardOpen] = useState<boolean>(false);
  const [infoMessages, setInfoMessages] = useState<string[]>(['Showing future appointments only']);

  const [params, setParams] = useState({
    repeat: false,
    sort: initialSortState.map(({ id, direction }) => `${id}.${direction}`),
    limit: 20,
  });

  const isFirstLoad = useRef(true);

  useEffect(() => {
    setTitle('Appointments');
    // Get appointments on mount
    load();

    // Get lookups on mount
    dispatch(
      getServiceTypes({
        deleted: {
          or: [true, false],
        },
        sort: ['deleted', 'name'],
      }),
    );
    dispatch(getStatuses());
    dispatch(getClients());
    dispatch(getClientPackages({ sort: ['name'], deleted: false }));
    dispatch(getClientReferrers({ sort: ['name'], deleted: false }));

    // Clean up component on unmount, clear reducer, etc.
    return () => {
      dispatch(clearAppointments());
      dispatch(clearMessage());
    };
  }, []);

  useEffect(() => {
    // If first load, do not run another load.
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }

    if (searchParams !== initialSearchState) {
      setInfoMessages([]);
      load();
    }
  }, [searchParams]);

  const load = (loadParams: { [key: string]: any } = {}) => {
    const newParams = {
      ...params,
      ...loadParams,
    };

    // Keep search and load params separate as search can be cleared. Don't want to retain values when nothing is passed back.
    dispatch(getAll({ ...newParams, ...searchParams }));
    setParams(newParams);
  };

  const sort = (params: SortState = []) => {
    load({
      sort: params.filter(({ direction }) => direction !== null).map(({ id, direction }) => `${id}.${direction}`),
    });
  };

  const download = (params: { [key: string]: any }, onProgress: (value: any) => void) =>
    exportAll({ params, onProgress });

  const search = (values: { [key: string]: any }) => setSearchParams(values);
  const estimate = async (values: { [key: string]: any }) => dispatch(getEstimate(values));

  const appointments = useAppSelector(selectAppointments);
  const appointmentsTotal = useAppSelector(selectAppointmentsTotal);
  const appointmentRowEstimate = useAppSelector(selectAppointmentRowEstimate);
  const errorResponse = useAppSelector(selectErrorResponse);
  const appointmentLoading = useAppSelector(selectAppointmentLoading);
  const isLoading = appointmentLoading === 'pending';
  return (
    <div className="p-2">
      <ContainerHeader icon={faBusinessTime} iconSize="1x" className="info" dataCy="appointments-header">
        Appointments
      </ContainerHeader>
      <ErrorResponse message={errorResponse} dataCy="appointments-errorResponse" />
      <div className="nav d-flex justify-content-between bg-light p-2 mt-2 mb-2">
        <div>
          <Button
            authTag={authTagUpdate}
            data-cy={'appointments-new-button'}
            size="sm"
            color="success"
            className="ms-2"
            href={'/appointments/new'}
          >
            <Icon className="me-2" icon={faPlus} />
            Add new (once off)
          </Button>
          <Button
            authTag={authTagUpdate}
            data-cy={'appointments-new-repeat-button'}
            size="sm"
            color="warning"
            className="ms-2"
            href={'/appointments/repeats/new'}
          >
            <Icon className="me-2" icon={faPlus} />
            Add Repeat Appointment
          </Button>
          <Button
            authTag={authTagViewAll}
            data-cy={'appointments-repeats-button'}
            size="sm"
            color="warning"
            className="ms-2"
            href={'/appointments/repeats'}
          >
            Repeat Appointments
          </Button>
          <Link to={`${REDIRECT_URL}/mail-merge-appointments.asp`}>
            <Button data-cy={'appointments-expand-view-button'} size="sm" className="ms-2">
              Expand View
            </Button>
          </Link>
        </div>
        <div className="d-flex">
          <ClickableIcon
            id="appointments-bulk-button"
            icon={faTableList}
            onClick={() => {
              setBulkUpdateWizardOpen(true);
            }}
            toolTip="Bulk Update"
            className="me-2"
            authTag={authTagBulkUpdate}
          />
          <Button
            data-cy={'appointments-search-button'}
            size="sm"
            className="me-2"
            onClick={() => setSearchBarOpen(!searchBarOpen)}
          >
            <Icon className="me-2" icon={faMagnifyingGlass} />
            {searchBarOpen ? 'Hide' : 'Search'}
          </Button>
          <Button
            data-cy={'appointments-export-button'}
            authTag={authTagExport}
            size="sm"
            className="me-2"
            onClick={() => setExportDialogOpen(!exportDialogOpen)}
          >
            <Icon className="me-2" icon={faFileExcel} />
            Export
          </Button>
        </div>
      </div>
      <ExportDialog
        data-cy={'appointments-export-dialog'}
        isOpen={exportDialogOpen}
        fields={exportFields}
        openingValues={searchParams}
        estimate={appointmentRowEstimate}
        onClose={() => setExportDialogOpen(false)}
        onEstimate={estimate}
        onDownload={(params, onProgress) => download(params, onProgress)}
      />
      <BulkUpdatePackageWizard
        isOpen={bulkUpdateWizardOpen}
        setIsOpen={(toggle) => {
          setBulkUpdateWizardOpen(toggle);
          if (!toggle) load();
        }}
        type="singles"
      />
      {infoMessages.length > 0 && (
        <ContainerBanner
          data-cy={'appointments-info-banner'}
          className="m-2"
          color="info"
          icon={faInfoCircle}
          iconSize="2x"
          messages={infoMessages}
          onClear={() => setInfoMessages([])}
        />
      )}
      <Collapse isOpen={searchBarOpen}>
        <SearchBar
          data-cy={'appointments-search-bar'}
          fields={hasViewAll ? allFields : commonFields}
          initialValues={searchParams}
          storageKey="appointments"
          onSubmit={search}
        />
      </Collapse>
      <TableView
        id="appointments"
        data={appointments as Row[]}
        columns={columns}
        readonly
        striped
        isLoading={isLoading}
        emptyIcon={faBusinessTime}
        emptyMessage={'No appointments found, please add one!'}
        allowSort={true}
        initialSortState={initialSortState}
        onSort={sort}
        allowPagination={true}
        onLoadMore={() => load({ limit: params.limit + 20 })}
        showCount={true}
        totalCount={appointmentsTotal}
      />
    </div>
  );
};

export default Appointments;
